import { useEffect, useState, useCallback, useMemo } from "react";
import { api, apiError } from "@/lib/api";
import MapView from "@/components/MapView";
import EventFeed from "@/components/work/EventFeed";
import { Loading, ErrorState } from "@/components/common/States";
import EmptyState from "@/components/common/EmptyState";
import { fmtMins, fmtTimeIST, fmtDayLabel } from "@/lib/dateRange";
import { AlertTriangle, Navigation } from "lucide-react";

const Stat = ({ label, value, hint, testid }) => (
  <div className="bg-white border border-slate-200/80 rounded-xl p-4" data-testid={testid}>
    <div className="text-[11px] font-semibold uppercase tracking-wider text-slate-500">{label}</div>
    <div className="mt-1 text-lg font-bold text-slate-900 tabular-nums">{value}</div>
    {hint && <div className="text-[11px] text-slate-400">{hint}</div>}
  </div>
);

function useMarkers(data) {
  return useMemo(() => {
    if (!data) return [];
    const out = [];
    data.sessions.forEach((s, i) => {
      const route = data.routes[i] || [];
      if (route.length) out.push({ lat: route[0][0], lng: route[0][1], color: "#0284C7", label: `Shift start · ${fmtTimeIST(s.start_at)}` });
      const last = route[route.length - 1];
      if (last && route.length > 1) {
        const live = s.status === "working";
        out.push({ lat: last[0], lng: last[1], color: live ? (s.stale ? "#D97706" : "#059669") : "#475569",
          label: live ? (s.stale ? `Last known location · stale (${s.minutes_since_update} min)` : "Current location") : `Shift end · ${fmtTimeIST(s.end_at)}` });
      }
    });
    data.stops.forEach((st) => out.push({ lat: st.lat, lng: st.lng, color: "#7C3AED", radius: 6,
      label: `Stop · ~${st.duration_min} min · ${fmtTimeIST(st.start)}–${fmtTimeIST(st.end)}` }));
    return out;
  }, [data]);
}

export default function DayRoute({ employeeId, date, refreshKey = 0 }) {
  const [data, setData] = useState(null);
  const [error, setError] = useState("");

  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/work/day", { params: { date, ...(employeeId ? { employee: employeeId } : {}) } });
      setData(data); setError("");
    } catch (e) { setError(apiError(e)); }
  }, [employeeId, date]);
  useEffect(() => { setData(null); load(); }, [load]);
  useEffect(() => { if (refreshKey) load(); }, [refreshKey, load]);
  useEffect(() => {
    if (!data?.summary?.working) return;
    const t = setInterval(load, 60000);
    return () => clearInterval(t);
  }, [data?.summary?.working, load]);

  const markers = useMarkers(data);
  if (error) return <ErrorState message={error} onRetry={load} />;
  if (!data) return <Loading label="Loading shift history…" />;
  const s = data.summary;

  return (
    <div className="space-y-4" data-testid="day-route">
      <div className="flex items-center justify-between gap-2">
        <h3 className="text-lg font-semibold text-slate-900">{data.employee.name} · {fmtDayLabel(data.date)}</h3>
        {s.working && <span className={`text-xs font-semibold rounded-full px-2.5 py-1 ${s.stale ? "bg-amber-100 text-amber-700" : "bg-emerald-100 text-emerald-700"}`} data-testid="day-route-live-badge">{s.stale ? "Location stale" : "Live"}</span>}
      </div>
      {s.stale && (
        <div className="flex items-start gap-3 rounded-xl border border-amber-300 bg-amber-50 p-4 text-sm text-amber-800" data-testid="stale-location-alert">
          <AlertTriangle className="h-5 w-5 shrink-0" />
          <div><b>Location is stale.</b> No GPS update for {s.minutes_since_update} min (threshold {data.stale_minutes} min). The position shown is the last known location — the device may be offline or location may be turned off.</div>
        </div>
      )}
      {data.sessions.length === 0 ? (
        <EmptyState title="No work recorded" description="No work session was started on this day." icon={Navigation} />
      ) : (
        <>
          <div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-8 gap-3">
            <Stat testid="day-stat-start" label="Shift start" value={fmtTimeIST(s.shift_start)} />
            <Stat testid="day-stat-end" label="Shift end" value={s.working ? "In progress" : fmtTimeIST(s.shift_end)} />
            <Stat testid="day-stat-duration" label="Duration" value={fmtMins(s.duration_min)} hint={`${s.sessions} session${s.sessions === 1 ? "" : "s"}`} />
            <Stat testid="day-stat-distance" label="Distance" value={`${(s.distance_km || 0).toFixed(1)} km`} hint="Estimated from GPS" />
            <Stat testid="day-stat-travel" label="Travel time" value={fmtMins(s.travel_min)} hint="Estimated" />
            <Stat testid="day-stat-stationary" label="Stationary" value={fmtMins(s.stationary_min)} hint="Estimated" />
            <Stat testid="day-stat-stops" label="Stops" value={s.stops} hint="≥ 5 min in one place" />
            <Stat testid="day-stat-untracked" label="No signal" value={fmtMins(s.untracked_min)} hint="Untracked time" />
          </div>
          <div className="grid lg:grid-cols-[1fr_22rem] gap-4">
            <MapView markers={markers} routes={data.routes} height={440} testid="day-route-map" />
            <EventFeed events={data.events} />
          </div>
        </>
      )}
    </div>
  );
}
